import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Document2026Service } from '../../services/document2026.service';
import { DocumentSection, Document2026 } from '../../models/document2026.model';
import * as JSZip from 'jszip';

@Component({
  selector: 'app-dokumente2026',
  templateUrl: './dokumente2026.component.html',
  styleUrls: ['./dokumente2026.component.css']
})
export class Dokumente2026Component implements OnInit {
  sections: DocumentSection[] = [];
  filteredSections: DocumentSection[] = [];
  expandedSections: Set<string> = new Set();
  isLoading: boolean = false;
  isAvailable: boolean = false;
  shouldShowExpiryMessage: boolean = false;
  remainingDays: number = 0;
  guid: string = '';
  errorMessage: string = '';
  searchQuery: string = '';

  constructor(
    private route: ActivatedRoute,
    private documentService: Document2026Service
  ) { }

  ngOnInit(): void {
    this.guid = this.route.snapshot.paramMap.get('guid') || '';
    
    // Prüfe GUID-Validierung
    if (!this.documentService.isValidGuid(this.guid)) {
      this.errorMessage = 'Ungültiger Zugangscode.';
      this.isAvailable = false;
      this.shouldShowExpiryMessage = false;
      return;
    }
    
    this.isAvailable = this.documentService.isAvailable();
    this.shouldShowExpiryMessage = this.documentService.shouldShowExpiryMessage();
    this.remainingDays = this.documentService.getRemainingDays();
    
    if (this.isAvailable) {
      this.loadDocuments();
    } else if (this.shouldShowExpiryMessage) {
      // Zeige Ablaufmeldung nur ab 8. Dezember (nach dem 23. Dezember)
      this.errorMessage = 'Diese Dokumente sind nicht mehr verfügbar. Die Verfügbarkeitsfrist ist abgelaufen.';
    } else {
      // Vor dem 9. Dezember: keine spezielle Meldung, einfach nichts anzeigen
      this.errorMessage = '';
    }
  }

  loadDocuments(): void {
    this.isLoading = true;
    this.documentService.getDocuments().subscribe({
      next: (sections) => {
        this.sections = sections;
        this.filteredSections = sections;
        // Alle Sektionen standardmäßig eingeklappt (leer lassen)
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Fehler beim Laden der Dokumente:', error);
        this.errorMessage = 'Fehler beim Laden der Dokumente.';
        this.isLoading = false;
      }
    });
  }

  onSearchChange(): void {
    if (!this.searchQuery || this.searchQuery.trim() === '') {
      this.filteredSections = this.sections;
      return;
    }

    const query = this.searchQuery.toLowerCase().trim();
    this.filteredSections = this.sections.map(section => {
      const filteredDocuments = section.documents.filter(doc => {
        const titleMatch = doc.title.toLowerCase().includes(query);
        const filenameMatch = doc.filename.toLowerCase().includes(query);
        return titleMatch || filenameMatch;
      });

      // Nur Sektionen mit gefilterten Dokumenten zurückgeben
      if (filteredDocuments.length > 0) {
        return {
          ...section,
          documents: filteredDocuments
        };
      }
      return null;
    }).filter(section => section !== null) as DocumentSection[];

    // Alle gefilterten Sektionen automatisch ausklappen
    this.filteredSections.forEach(section => {
      this.expandedSections.add(section.id);
    });
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.filteredSections = this.sections;
    // Alle Sektionen wieder einklappen
    this.expandedSections.clear();
  }

  getSearchResultsCount(): number {
    return this.filteredSections.reduce((sum, s) => sum + s.documents.length, 0);
  }

  toggleSection(sectionId: string): void {
    if (this.expandedSections.has(sectionId)) {
      this.expandedSections.delete(sectionId);
    } else {
      this.expandedSections.add(sectionId);
    }
  }

  isSectionExpanded(sectionId: string): boolean {
    return this.expandedSections.has(sectionId);
  }

  downloadDocument(doc: Document2026): void {
    this.documentService.loadPdfAsBlob(doc.assetPath).subscribe({
      next: (blob) => {
        if (!blob || blob.size === 0) {
          console.error('Leerer Blob erhalten für:', doc.filename);
          alert('Fehler: Die Datei konnte nicht geladen werden.');
          return;
        }
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Fehler beim Download:', error);
        console.error('Asset-Pfad:', doc.assetPath);
        console.error('Dateiname:', doc.filename);
        if (error.status === 404) {
          alert(`Die Datei "${doc.filename}" wurde nicht gefunden. Bitte kontaktieren Sie den Administrator.`);
        } else if (error.status === 0) {
          alert('Netzwerkfehler: Bitte überprüfen Sie Ihre Internetverbindung.');
        } else {
          alert(`Fehler beim Download des Dokuments: ${error.message || 'Unbekannter Fehler'}`);
        }
      }
    });
  }

  downloadSectionAsZip(section: DocumentSection): void {
    if (section.documents.length === 0) {
      return;
    }

    this.isLoading = true;
    this.documentService.loadAllPdfsAsBlobs(section.documents).subscribe({
      next: (results) => {
        const zip = new JSZip();
        
        results.forEach(result => {
          if (result && result.blob) {
            zip.file(result.document.filename, result.blob);
          }
        });

        const zipFileName = section.title.replace(/[^a-z0-9]/gi, '_') + '.zip';
        zip.generateAsync({ type: 'blob' }).then((content) => {
          const url = window.URL.createObjectURL(content);
          const link = document.createElement('a');
          link.href = url;
          link.download = zipFileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          this.isLoading = false;
        }).catch((error) => {
          console.error('Fehler beim Erstellen der ZIP-Datei:', error);
          alert('Fehler beim Erstellen der ZIP-Datei.');
          this.isLoading = false;
        });
      },
      error: (error) => {
        console.error('Fehler beim Laden der PDFs:', error);
        alert('Fehler beim Laden der Dokumente für die ZIP-Datei.');
        this.isLoading = false;
      }
    });
  }

  downloadAllAsZip(): void {
    const sectionsToUse = this.searchQuery ? this.filteredSections : this.sections;
    if (sectionsToUse.length === 0) {
      return;
    }

    this.isLoading = true;
    const allDocuments: Document2026[] = [];
    sectionsToUse.forEach(section => {
      allDocuments.push(...section.documents);
    });

    this.documentService.loadAllPdfsAsBlobs(allDocuments).subscribe({
      next: (results) => {
        const zip = new JSZip();
        
        // Erstelle Ordnerstruktur in der ZIP
        sectionsToUse.forEach(section => {
          if (section.documents.length > 0) {
            const folderName = section.title.replace(/[^a-z0-9]/gi, '_');
            section.documents.forEach(doc => {
              const result = results.find(r => r && r.document.id === doc.id);
              if (result && result.blob) {
                zip.file(`${folderName}/${result.document.filename}`, result.blob);
              }
            });
          }
        });

        zip.generateAsync({ type: 'blob' }).then((content) => {
          const url = window.URL.createObjectURL(content);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'Muster-Dokumente.zip';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          this.isLoading = false;
        }).catch((error) => {
          console.error('Fehler beim Erstellen der ZIP-Datei:', error);
          alert('Fehler beim Erstellen der ZIP-Datei.');
          this.isLoading = false;
        });
      },
      error: (error) => {
        console.error('Fehler beim Laden der PDFs:', error);
        alert('Fehler beim Laden der Dokumente für die ZIP-Datei.');
        this.isLoading = false;
      }
    });
  }
}

